import { nbsp } from '@orus.eu/char'
import { assertDefinedAndNotNull } from '@orus.eu/error'

import type {
  ActivitiesConfigVersionFormat,
  InsuranceProduct,
  InsuranceProductVersion,
  InsuranceProductVersionFormat,
  InsuranceProductVersionWithDateFormat,
} from '../../model/model.ts'

import type { MrpaSegment, mrpaSegmentShortLabel } from './activities/activity-segment.ts'
import type { MrpaPolicyItems, MrpaPolicyItemsState } from './mrpa-insurance.ts'

export type MrpaActivitiesConfigVersionFormat = ActivitiesConfigVersionFormat<'mrpa'>

import type { MrpaPricingVersion } from './mrpa-pricing-versions.ts'
import { mrpaPolicyItemsV1_0_0 } from './v1_0_0/policy-items.ts'
import {
  mrpa_V1_0_0_StaticFilesIds,
  mrpa_V1_0_1_StaticFilesIds,
  mrpa_V1_0_2_StaticFilesIds,
  mrpa_V1_0_3_StaticFilesIds,
  mrpa_V1_0_4_StaticFilesIds,
  mrpa_V1_1_0_StaticFilesIds,
  mrpa_V1_2_0_StaticFilesIds,
  mrpa_V1_3_0_StaticFilesIds,
} from './v1_0_0/static-files.ts'

export const mrpaProductName = 'mrpa'
export type MrpaProductName = typeof mrpaProductName

export const mrpaProductMajorVersions = ['mrpa-v1'] as const satisfies `${MrpaProductName}-v${number}`[]

export type MrpaProductMajorVersions = (typeof mrpaProductMajorVersions)[number]

export const mrpaProductVersions = [
  'mrpa-1.0.0-2025-11-01',
  'mrpa-1.0.1-2025-12-17',
  'mrpa-1.0.2-2026-01-13',
  'mrpa-1.0.3-2026-04-14',
  'mrpa-1.0.4-2026-05-12',
  'mrpa-1.1.0-2026-07-16',
  'mrpa-1.2.0-2026-07-27',
  'mrpa-1.3.0-2026-08-05',
] as const satisfies `${MrpaProductName}-${InsuranceProductVersionFormat}-${InsuranceProductVersionWithDateFormat}`[]

export type MrpaProductVersion = (typeof mrpaProductVersions)[number]

export const allMrpaStaticFilesIds = [
  ...mrpa_V1_0_0_StaticFilesIds,
  ...mrpa_V1_0_1_StaticFilesIds,
  ...mrpa_V1_0_2_StaticFilesIds,
  ...mrpa_V1_0_3_StaticFilesIds,
  ...mrpa_V1_0_4_StaticFilesIds,
] as const

export type MrpaStaticFilesId = (typeof allMrpaStaticFilesIds)[number]

export const allMrpaStaticFilesReferences = {
  'mrpa-v1_0_0-general-terms': `Dispositions communes${nbsp}: «${nbsp}CONDITIONS GÉNÉRALES - DISPOSITIONS COMMUNES ASSURANCE MULTIRISQUE PROFESSIONNELLE FR301 – 10.2022${nbsp}»`,
  'mrpa-v1_0_0-ipid': `«${nbsp}Fiche IPID_Assurance Multirisque Professionnelle 09/2023${nbsp}»`,
  'mrpa-v1_0_4-ipid': `«${nbsp}Fiche IPID_Assurance Multirisque Professionnelle 09/2023${nbsp}»`,
  'mrpa-v1_0_0-assistance-special-terms': `«${nbsp}Conventions spéciales – Assistance – 11.2025${nbsp}»`,
  'mrpa-v1_0_0-hospitality-special-terms': `Activités CHR${nbsp}: «ASSURANCE MULTIRISQUE PROFESSIONNELLE CONVENTIONS SPECIALES « HOTELS & CAFES- RESTAURANTS${nbsp}» FR302 – 10.2022${nbsp}»`,
  'mrpa-v1_0_0-manufacturing-special-terms': `Activités industrielles${nbsp}: «${nbsp}Assurance Multirisque Professionnelle – Conventions Spéciales « Activités industrielles » – 09/2022(FR303_10.22)»`,
  'mrpa-v1_0_0-dab-pf-general-terms': `Dommages aux biens et Pertes financières${nbsp}: «${nbsp}CONDITIONS GENERALES ASSURANCE MULTIRISQUE PROFESSIONNELLE MODULE « DOMMAGES AUX BIENS ET PERTES FINANCIERES » FR22 – 03.2024${nbsp}»`,
  'mrpa-v1_0_0-rce-general-terms': `Module Responsabilité Civile Entreprise${nbsp}: «CONDITIONS GENERALES ASSURANCE MULTIRISQUE PROFESSIONNELLE MODULE « RESPONSABILITE CIVILE ENTREPRISE » FR106 – 04.2023${nbsp}»`,
  'mrpa-v1_0_1-general-terms': `Dispositions communes${nbsp}: «${nbsp}CONDITIONS GÉNÉRALES - DISPOSITIONS COMMUNES ASSURANCE MULTIRISQUE PROFESSIONNELLE FR301 – 10.2022${nbsp}»`,

  'mrpa-v1_0_2-assistance-special-terms': `«${nbsp}Conventions spéciales – Assistance – 11.2025${nbsp}»`,

  'mrpa-v1_0_3-assistance-special-terms': `«${nbsp}Conventions spéciales – Assistance – 11.2025${nbsp}»`,
  'mrpa-v1_0_3-dab-pf-general-terms': `Dommages aux biens et Pertes financières${nbsp}: «${nbsp}CONDITIONS GENERALES ASSURANCE MULTIRISQUE PROFESSIONNELLE MODULE « DOMMAGES AUX BIENS ET PERTES FINANCIERES » FR22 – 03.2024${nbsp}»`,
  'mrpa-v1_0_3-general-terms': `Dispositions communes${nbsp}: «${nbsp}CONDITIONS GÉNÉRALES - DISPOSITIONS COMMUNES ASSURANCE MULTIRISQUE PROFESSIONNELLE FR301 – 10.2022${nbsp}»`,
  'mrpa-v1_0_3-hospitality-special-terms': `Activités CHR${nbsp}: «ASSURANCE MULTIRISQUE PROFESSIONNELLE CONVENTIONS SPECIALES « HOTELS & CAFES- RESTAURANTS${nbsp}» FR302 – 10.2022${nbsp}»`,
  'mrpa-v1_0_3-manufacturing-special-terms': `Activités industrielles${nbsp}: «${nbsp}Assurance Multirisque Professionnelle – Conventions Spéciales « Activités industrielles » – 09/2022(FR303_10.22)»`,
  'mrpa-v1_0_3-rce-general-terms': `Module Responsabilité Civile Entreprise${nbsp}: «CONDITIONS GENERALES ASSURANCE MULTIRISQUE PROFESSIONNELLE MODULE « RESPONSABILITE CIVILE ENTREPRISE » FR106 – 04.2023${nbsp}»`,

  'mrpa-v1_0_4-assistance-special-terms': `«${nbsp}Conventions spéciales – Assistance – 11.2025${nbsp}»`,
} as const satisfies Record<MrpaStaticFilesId, string>

export const mrpaStaticFilesNameByStaticFileId = {
  'mrpa-v1_0_0-general-terms': 'Conditions générales – Dispositions communes - FR301 – 10.2022',
  'mrpa-v1_0_1-general-terms': 'Conditions générales – Dispositions communes - FR301 – 10.2022',
  'mrpa-v1_0_0-ipid': 'Fiche IPID_Assurance Multirisque Professionnelle 09/2023',
  'mrpa-v1_0_4-ipid': 'Fiche IPID_Assurance Multirisque Professionnelle 09/2023',
  'mrpa-v1_0_0-assistance-special-terms': 'Conventions spéciales – Assistance',
  'mrpa-v1_0_2-assistance-special-terms': 'Conventions spéciales – Assistance',
  'mrpa-v1_0_0-hospitality-special-terms': 'Conventions spéciales – Hotels & Cafés-Restaurants - FR302 – 10.2022',
  'mrpa-v1_0_0-manufacturing-special-terms': 'Conventions spéciales – Activités industrielles - 09/2022 (FR303_10.22)',
  'mrpa-v1_0_0-dab-pf-general-terms':
    'Conditions générales – Module « Dommages aux biens et pertes financières » - FR22 – 03.2024',
  'mrpa-v1_0_0-rce-general-terms': 'Conditions générales – Responsabilité Civile Entreprise - FR106 – 04.2023',

  'mrpa-v1_0_3-assistance-special-terms': 'Conventions spéciales – Assistance',
  'mrpa-v1_0_3-dab-pf-general-terms':
    'Conditions générales – Module « Dommages aux biens et pertes financières » - FR22 – 03.2024',
  'mrpa-v1_0_3-general-terms': 'Conditions générales – Dispositions communes - FR301 – 10.2022',
  'mrpa-v1_0_3-hospitality-special-terms': 'Conventions spéciales – Hotels & Cafés-Restaurants - FR302 – 10.2022',
  'mrpa-v1_0_3-manufacturing-special-terms': 'Conventions spéciales – Activités industrielles - 09/2022 (FR303_10.22)',
  'mrpa-v1_0_3-rce-general-terms': 'Conditions générales – Responsabilité Civile Entreprise - FR106 – 04.2023',

  'mrpa-v1_0_4-assistance-special-terms': 'Conventions spéciales – Assistance',
} as const satisfies Record<MrpaStaticFilesId, string>

export const mrpaStaticFilesIdsByProductVersion = {
  'mrpa-1.0.0-2025-11-01': mrpa_V1_0_0_StaticFilesIds,
  'mrpa-1.0.1-2025-12-17': mrpa_V1_0_1_StaticFilesIds,
  'mrpa-1.0.2-2026-01-13': mrpa_V1_0_2_StaticFilesIds,
  'mrpa-1.0.3-2026-04-14': mrpa_V1_0_3_StaticFilesIds,
  'mrpa-1.0.4-2026-05-12': mrpa_V1_0_4_StaticFilesIds,
  'mrpa-1.1.0-2026-07-16': mrpa_V1_1_0_StaticFilesIds,
  'mrpa-1.2.0-2026-07-27': mrpa_V1_2_0_StaticFilesIds,
  'mrpa-1.3.0-2026-08-05': mrpa_V1_3_0_StaticFilesIds,
} as const satisfies Record<MrpaProductVersion, readonly MrpaStaticFilesId[]>

export const mrpaRiskCarrierLabelByProductVersion = {
  'mrpa-1.0.0-2025-11-01': {
    ARTISAN: 'mrpa-artisan-v1',
    BEAUTY_WELLNESS: 'mrpa-wellness-v1',
    EDUCATION: 'mrpa-education-v1',
    HOSPITALITY: 'mrpa-hospitality-v1',
    RETAIL: 'mrpa-retail-v1',
    SERVICES: 'mrpa-services-v1',
    OFFICE: 'mrpa-office-v1',
    MANUFACTURING: 'mrpa-manufacturing-v1',
    HEALTH_SOCIAL_ACTIVITIES: 'mrpa-health-social-v1',
    RECREATION_ENTERTAINMENT: 'mrpa-entertainment-v1',
  },
  'mrpa-1.0.1-2025-12-17': {
    ARTISAN: 'mrpa-artisan-v1',
    BEAUTY_WELLNESS: 'mrpa-wellness-v1',
    EDUCATION: 'mrpa-education-v1',
    HOSPITALITY: 'mrpa-hospitality-v1',
    RETAIL: 'mrpa-retail-v1',
    SERVICES: 'mrpa-services-v1',
    OFFICE: 'mrpa-office-v1',
    MANUFACTURING: 'mrpa-manufacturing-v1',
    HEALTH_SOCIAL_ACTIVITIES: 'mrpa-health-social-v1',
    RECREATION_ENTERTAINMENT: 'mrpa-entertainment-v1',
  },
  'mrpa-1.0.2-2026-01-13': {
    ARTISAN: 'mrpa-artisan-v1',
    BEAUTY_WELLNESS: 'mrpa-wellness-v1',
    EDUCATION: 'mrpa-education-v1',
    HOSPITALITY: 'mrpa-hospitality-v1',
    RETAIL: 'mrpa-retail-v1',
    SERVICES: 'mrpa-services-v1',
    OFFICE: 'mrpa-office-v1',
    MANUFACTURING: 'mrpa-manufacturing-v1',
    HEALTH_SOCIAL_ACTIVITIES: 'mrpa-health-social-v1',
    RECREATION_ENTERTAINMENT: 'mrpa-entertainment-v1',
  },
  'mrpa-1.0.3-2026-04-14': {
    ARTISAN: 'mrpa-artisan-v1',
    BEAUTY_WELLNESS: 'mrpa-wellness-v1',
    EDUCATION: 'mrpa-education-v1',
    HOSPITALITY: 'mrpa-hospitality-v1',
    RETAIL: 'mrpa-retail-v1',
    SERVICES: 'mrpa-services-v1',
    OFFICE: 'mrpa-office-v1',
    MANUFACTURING: 'mrpa-manufacturing-v1',
    HEALTH_SOCIAL_ACTIVITIES: 'mrpa-health-social-v1',
    RECREATION_ENTERTAINMENT: 'mrpa-entertainment-v1',
  },
  'mrpa-1.0.4-2026-05-12': {
    ARTISAN: 'mrpa-artisan-v1',
    BEAUTY_WELLNESS: 'mrpa-wellness-v1',
    EDUCATION: 'mrpa-education-v1',
    HOSPITALITY: 'mrpa-hospitality-v1',
    RETAIL: 'mrpa-retail-v1',
    SERVICES: 'mrpa-services-v1',
    OFFICE: 'mrpa-office-v1',
    MANUFACTURING: 'mrpa-manufacturing-v1',
    HEALTH_SOCIAL_ACTIVITIES: 'mrpa-health-social-v1',
    RECREATION_ENTERTAINMENT: 'mrpa-entertainment-v1',
  },
  'mrpa-1.1.0-2026-07-16': {
    ARTISAN: 'mrpa-artisan-v1',
    BEAUTY_WELLNESS: 'mrpa-wellness-v1',
    EDUCATION: 'mrpa-education-v1',
    HOSPITALITY: 'mrpa-hospitality-v1',
    RETAIL: 'mrpa-retail-v1',
    SERVICES: 'mrpa-services-v1',
    OFFICE: 'mrpa-office-v1',
    MANUFACTURING: 'mrpa-manufacturing-v1',
    HEALTH_SOCIAL_ACTIVITIES: 'mrpa-health-social-v1',
    RECREATION_ENTERTAINMENT: 'mrpa-entertainment-v1',
  },
  'mrpa-1.2.0-2026-07-27': {
    ARTISAN: 'mrpa-artisan-v1',
    BEAUTY_WELLNESS: 'mrpa-wellness-v1',
    EDUCATION: 'mrpa-education-v1',
    HOSPITALITY: 'mrpa-hospitality-v1',
    RETAIL: 'mrpa-retail-v1',
    SERVICES: 'mrpa-services-v1',
    OFFICE: 'mrpa-office-v1',
    MANUFACTURING: 'mrpa-manufacturing-v1',
    HEALTH_SOCIAL_ACTIVITIES: 'mrpa-health-social-v1',
    RECREATION_ENTERTAINMENT: 'mrpa-entertainment-v1',
  },
  'mrpa-1.3.0-2026-08-05': {
    ARTISAN: 'mrpa-artisan-v1',
    BEAUTY_WELLNESS: 'mrpa-wellness-v1',
    EDUCATION: 'mrpa-education-v1',
    HOSPITALITY: 'mrpa-hospitality-v1',
    RETAIL: 'mrpa-retail-v1',
    SERVICES: 'mrpa-services-v1',
    OFFICE: 'mrpa-office-v1',
    MANUFACTURING: 'mrpa-manufacturing-v1',
    HEALTH_SOCIAL_ACTIVITIES: 'mrpa-health-social-v1',
    RECREATION_ENTERTAINMENT: 'mrpa-entertainment-v1',
  },
} as const satisfies Record<
  MrpaProductVersion,
  { [key in MrpaSegment]: `${MrpaProductName}-${(typeof mrpaSegmentShortLabel)[key]}-v${number}` }
>

export const mrpaRiskCarrierProductTypeNames = Object.entries(mrpaRiskCarrierLabelByProductVersion)
  .map(([_key, value]) => value)
  .flatMap((value) => Object.values(value))

export type MrpaRiskCarrierProductType = (typeof mrpaRiskCarrierProductTypeNames)[number]

export const mrpaProduct_V_1_0_0 = {
  version: 'mrpa-1.0.0-2025-11-01',
  description: 'Version initiale du produit MRPA',
  policyItems: mrpaPolicyItemsV1_0_0,
  staticFilesId: [...mrpa_V1_0_0_StaticFilesIds],
  pricingVersion: ['mrpa-1.0.0-2025-11-01'],
  activitiesConfigVersion: 'activities-config-mrpa-1.0.0-2026-01-13',
} as const satisfies InsuranceProductVersion<
  MrpaProductName,
  MrpaProductName,
  MrpaProductVersion,
  MrpaPolicyItems,
  MrpaStaticFilesId,
  MrpaPricingVersion,
  MrpaActivitiesConfigVersionFormat,
  MrpaPolicyItemsState
>

export const mrpaProduct_V_1_0_1 = {
  version: 'mrpa-1.0.1-2025-12-17',
  description: 'Version qui met à jour les CG du produit MRPA',
  policyItems: mrpaPolicyItemsV1_0_0,
  staticFilesId: [...mrpa_V1_0_1_StaticFilesIds],
  pricingVersion: ['mrpa-1.0.0-2025-11-01'],
  activitiesConfigVersion: 'activities-config-mrpa-1.0.0-2026-01-13',
} as const satisfies InsuranceProductVersion<
  MrpaProductName,
  MrpaProductName,
  MrpaProductVersion,
  MrpaPolicyItems,
  MrpaStaticFilesId,
  MrpaPricingVersion,
  MrpaActivitiesConfigVersionFormat,
  MrpaPolicyItemsState
>

export const mrpaProduct_V_1_0_2 = {
  version: 'mrpa-1.0.2-2026-01-13',
  description: 'Version qui met à jour les CG du produit MRPA',
  policyItems: mrpaPolicyItemsV1_0_0,
  staticFilesId: [...mrpa_V1_0_2_StaticFilesIds],
  pricingVersion: ['mrpa-1.0.0-2025-11-01'],
  activitiesConfigVersion: 'activities-config-mrpa-1.0.0-2026-01-13',
} as const satisfies InsuranceProductVersion<
  MrpaProductName,
  MrpaProductName,
  MrpaProductVersion,
  MrpaPolicyItems,
  MrpaStaticFilesId,
  MrpaPricingVersion,
  MrpaActivitiesConfigVersionFormat,
  MrpaPolicyItemsState
>

export const mrpaProduct_V_1_0_3 = {
  version: 'mrpa-1.0.3-2026-04-14',
  description:
    'Version qui met à jour les CG du produit MRPA en rassemblant les PDFs de CG et CS et en implémentant un footer dynamique',
  policyItems: mrpaPolicyItemsV1_0_0,
  staticFilesId: [...mrpa_V1_0_3_StaticFilesIds],
  pricingVersion: ['mrpa-1.0.0-2025-11-01'],
  activitiesConfigVersion: 'activities-config-mrpa-1.0.0-2026-01-13',
} as const satisfies InsuranceProductVersion<
  MrpaProductName,
  MrpaProductName,
  MrpaProductVersion,
  MrpaPolicyItems,
  MrpaStaticFilesId,
  MrpaPricingVersion,
  MrpaActivitiesConfigVersionFormat,
  MrpaPolicyItemsState
>

export const mrpaProduct_V_1_0_4 = {
  version: 'mrpa-1.0.4-2026-05-12',
  description: 'Version qui met à jour les CS Assistance du produit MRPA, suite à une erreure dans le CS assistance',
  policyItems: mrpaPolicyItemsV1_0_0,
  staticFilesId: [...mrpa_V1_0_4_StaticFilesIds],
  pricingVersion: ['mrpa-1.0.0-2025-11-01'],
  activitiesConfigVersion: 'activities-config-mrpa-1.0.0-2026-01-13',
} as const satisfies InsuranceProductVersion<
  MrpaProductName,
  MrpaProductName,
  MrpaProductVersion,
  MrpaPolicyItems,
  MrpaStaticFilesId,
  MrpaPricingVersion,
  MrpaActivitiesConfigVersionFormat,
  MrpaPolicyItemsState
>

/* Same product as v1.0.4 (no changes to coverage/pricing/config). This is
 * just a minor bump introduced for Project Churn's [Measure 5](https://app.notion.com/p/orus-team/Tech-Scoping-ac90ab24de318301be6701911fcc734b?source=copy_link#f701560538b342b98ab534128adbbb70)
 * so the annual-payment requirement applies only from this version onwards,
 * protecting already-signed subscriptions.
 */
export const mrpaProduct_V_1_1_0 = {
  version: 'mrpa-1.1.0-2026-07-16',
  description: 'Bump de version pour Measure 5 de Project Churn — aucun changement de garantie/tarif',
  policyItems: mrpaPolicyItemsV1_0_0,
  staticFilesId: [...mrpa_V1_1_0_StaticFilesIds],
  pricingVersion: ['mrpa-1.0.0-2025-11-01'],
  activitiesConfigVersion: 'activities-config-mrpa-1.0.0-2026-01-13',
} as const satisfies InsuranceProductVersion<
  MrpaProductName,
  MrpaProductName,
  MrpaProductVersion,
  MrpaPolicyItems,
  MrpaStaticFilesId,
  MrpaPricingVersion,
  MrpaActivitiesConfigVersionFormat,
  MrpaPolicyItemsState
>

/* Same product as v1.1.0 (no changes to coverage/pricing/config). This is
 * just a minor bump introduced to require anti-fire measures for MRPA
 * so the "étude de dossier" application-validation flow applies to MRPA
 * self-onboarding only from this version onwards, protecting already-signed
 * subscriptions.
 */
export const mrpaProduct_V_1_2_0 = {
  version: 'mrpa-1.2.0-2026-07-27',
  description:
    'Bump de version pour la validation de dossiers MRPA (anti-churn project) — aucun changement de garantie/tarif',
  policyItems: mrpaPolicyItemsV1_0_0,
  staticFilesId: [...mrpa_V1_2_0_StaticFilesIds],
  pricingVersion: ['mrpa-1.0.0-2025-11-01'],
  activitiesConfigVersion: 'activities-config-mrpa-1.0.0-2026-01-13',
} as const satisfies InsuranceProductVersion<
  MrpaProductName,
  MrpaProductName,
  MrpaProductVersion,
  MrpaPolicyItems,
  MrpaStaticFilesId,
  MrpaPricingVersion,
  MrpaActivitiesConfigVersionFormat,
  MrpaPolicyItemsState
>

/* Same product as v1.2.0 (no changes to coverage/pricing/config). This is
 * just a minor bump introduced so the SEPA-only payment-method restriction applies only from this
 * version onwards, protecting already-signed subscriptions paying by card.
 */
export const mrpaProduct_V_1_3_0 = {
  version: 'mrpa-1.3.0-2026-08-05',
  description:
    'Bump de version pour la restriction de paiement SEPA uniquement (Project Churn) — aucun changement de garantie/tarif',
  policyItems: mrpaPolicyItemsV1_0_0,
  staticFilesId: [...mrpa_V1_3_0_StaticFilesIds],
  pricingVersion: ['mrpa-1.0.0-2025-11-01'],
  activitiesConfigVersion: 'activities-config-mrpa-1.0.0-2026-01-13',
} as const satisfies InsuranceProductVersion<
  MrpaProductName,
  MrpaProductName,
  MrpaProductVersion,
  MrpaPolicyItems,
  MrpaStaticFilesId,
  MrpaPricingVersion,
  MrpaActivitiesConfigVersionFormat,
  MrpaPolicyItemsState
>

export const mrpaProduct = {
  id: 'mrpa',
  versions: {
    'mrpa-1.0.0-2025-11-01': mrpaProduct_V_1_0_0,
    'mrpa-1.0.1-2025-12-17': mrpaProduct_V_1_0_1,
    'mrpa-1.0.2-2026-01-13': mrpaProduct_V_1_0_2,
    'mrpa-1.0.3-2026-04-14': mrpaProduct_V_1_0_3,
    'mrpa-1.0.4-2026-05-12': mrpaProduct_V_1_0_4,
    'mrpa-1.1.0-2026-07-16': mrpaProduct_V_1_1_0,
    'mrpa-1.2.0-2026-07-27': mrpaProduct_V_1_2_0,
    'mrpa-1.3.0-2026-08-05': mrpaProduct_V_1_3_0,
  },
} as const satisfies InsuranceProduct<
  MrpaProductName,
  MrpaProductName,
  MrpaProductVersion,
  MrpaPolicyItems,
  MrpaStaticFilesId,
  MrpaPricingVersion,
  MrpaActivitiesConfigVersionFormat,
  MrpaPolicyItemsState
>

export type MrpaProduct = typeof mrpaProduct

export type MrpaPolicyItemsDefinitions = MrpaProduct['versions'][MrpaProductVersion]['policyItems']

/**
 * Whether a given MRPA product version may be applied to existing unsigned subscriptions — i.e. quotes created before that
 * version that the bump trigger migrates forward while they are still unsigned. New-business-only versions must
 * return `false` so that existing unsigned subscriptions are never migrated onto them.
 *
 */
export function isMrpaProductVersionApplicableToOldSubscriptions(version: MrpaProductVersion): boolean {
  switch (version) {
    case 'mrpa-1.0.0-2025-11-01':
    case 'mrpa-1.0.1-2025-12-17':
    case 'mrpa-1.0.2-2026-01-13':
    case 'mrpa-1.0.3-2026-04-14':
    case 'mrpa-1.0.4-2026-05-12':
      return true
    case 'mrpa-1.1.0-2026-07-16':
    case 'mrpa-1.2.0-2026-07-27':
    case 'mrpa-1.3.0-2026-08-05':
      return false
  }
}

export function getLatestMrpaProductVersionForOldSubscriptions(): MrpaProductVersion {
  const applicableVersions = mrpaProductVersions.filter(isMrpaProductVersionApplicableToOldSubscriptions)
  return assertDefinedAndNotNull(
    applicableVersions[applicableVersions.length - 1],
    'No MRPA product version is applicable to existing unsigned subscriptions',
    {
      product: 'mrpa',
      productVersions: mrpaProductVersions,
    },
  )
}
